/* ============================================================================
   TOTH TRUST WALL · PRO  —  JavaScript
   ============================================================================
   Comportamentos:
     1. Reveal on scroll (IntersectionObserver)
     2. Stat counters animados
     3. Hero Trinity carousel (mobile swipe + dots)
     4. Marquee filter (filtros + pause)
     5. Logo card tap-reveal (mobile)
     6. Testimonial rotation (auto + manual + swipe)
     7. Theme editor live integration

   Vanilla JS · zero deps · respeita prefers-reduced-motion
   ============================================================================ */

(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ========================================================================
     1 · REVEAL ON SCROLL (entrada)
     ======================================================================== */
  function initReveal(section) {
    if (REDUCED || !('IntersectionObserver' in window)) {
      section.classList.add('is-visible');
      return;
    }

    // delays as inline CSS variable
    section.querySelectorAll('[data-ttw-delay]').forEach((el) => {
      el.style.setProperty('--ttw-anim-delay', el.dataset.ttwDelay + 'ms');
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.classList.add('is-visible');
            io.unobserve(section);
          }
        });
      },
      { threshold: 0.08 }
    );
    io.observe(section);
  }

  /* ========================================================================
     2 · STAT COUNTERS
     ======================================================================== */
  function initCounters(section) {
    const counters = section.querySelectorAll('[data-ttw-counter]');
    if (!counters.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      counters.forEach((el) => {
        el.textContent = el.dataset.ttwCounterText || el.dataset.ttwCounter;
      });
      return;
    }

    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

    const animate = (el) => {
      const target = parseInt(el.dataset.ttwCounter, 10);
      if (isNaN(target)) {
        el.textContent = el.dataset.ttwCounterText || '';
        return;
      }
      const duration = 2000;
      const startTime = performance.now();
      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutQuart(progress);
        const current = Math.floor(target * eased);
        el.textContent = current.toLocaleString('pt-BR');
        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          el.textContent = target.toLocaleString('pt-BR');
        }
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach((el) => io.observe(el));
  }

  /* ========================================================================
     3 · HERO TRINITY CAROUSEL (mobile swipe + dots)
     ======================================================================== */
  function initHeroes(section) {
    const grid = section.querySelector('[data-ttw-heroes]');
    const dots = section.querySelectorAll('[data-ttw-dot]');
    if (!grid || !dots.length) return;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    const items = grid.children;

    const updateActiveDot = () => {
      if (!isMobile()) return;
      const scrollLeft = grid.scrollLeft;
      const cardWidth = items[0] ? items[0].getBoundingClientRect().width : 0;
      const padding = 16;
      const activeIndex = Math.round(scrollLeft / (cardWidth + padding));
      dots.forEach((dot, idx) => {
        const isActive = idx === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    grid.addEventListener('scroll', () => {
      window.requestAnimationFrame(updateActiveDot);
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.ttwDot, 10);
        if (items[idx]) {
          items[idx].scrollIntoView({
            behavior: REDUCED ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'start',
          });
        }
      });
    });
  }

  /* ========================================================================
     4 · MARQUEE FILTER + tap-reveal
     ======================================================================== */
  function initMarquee(section) {
    const marquee = section.querySelector('[data-ttw-marquee]');
    if (!marquee) return;

    const filters = section.querySelectorAll('[data-ttw-filter]');
    const cards = marquee.querySelectorAll('[data-ttw-vertical]');

    /* Filters */
    if (filters.length && cards.length) {
      filters.forEach((btn) => {
        btn.addEventListener('click', () => {
          const filter = btn.dataset.ttwFilter;
          filters.forEach((b) => {
            const active = b === btn;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          cards.forEach((card) => {
            const cardVert = card.dataset.ttwVertical;
            const match = filter === 'all' || cardVert === filter;
            card.classList.toggle('is-filtered-out', !match);
          });
          // Pause marquee briefly when filtering
          if (filter !== 'all') {
            marquee.classList.add('is-paused');
          } else {
            marquee.classList.remove('is-paused');
          }
        });
      });
    }

    /* Tap reveal (mobile) — toggles is-revealed on card */
    const isTouchOnly = window.matchMedia('(hover: none)').matches;
    if (isTouchOnly) {
      cards.forEach((card) => {
        const reveal = card.querySelector('.ttw-pro-card__reveal');
        if (!reveal) return; // sem conteúdo de reveal, ignora
        card.addEventListener('click', (e) => {
          // permite click em link interno do reveal
          if (e.target.closest('.ttw-pro-card__link')) return;
          const wasRevealed = card.classList.contains('is-revealed');
          // close all others
          cards.forEach((c) => c.classList.remove('is-revealed'));
          if (!wasRevealed) {
            card.classList.add('is-revealed');
            // pause marquee while revealed
            const row = card.closest('.ttw-pro__row');
            if (row) row.classList.add('is-paused');
            // auto-close after 6s
            setTimeout(() => {
              card.classList.remove('is-revealed');
              if (row) row.classList.remove('is-paused');
            }, 6000);
          }
        });
      });
    }
  }

  /* ========================================================================
     5 · TESTIMONIAL ROTATION
     ======================================================================== */
  function initTestimonials(section) {
    const stage = section.querySelector('[data-ttw-testimonials]');
    if (!stage) return;

    const slides = stage.querySelectorAll('[data-ttw-testimonial-slide]');
    const dots = stage.querySelectorAll('[data-ttw-testimonial-dot]');
    const prev = stage.querySelector('[data-ttw-testimonial-prev]');
    const next = stage.querySelector('[data-ttw-testimonial-next]');
    if (slides.length < 2) return;

    let current = 0;
    let autoTimer = null;
    const AUTO_DELAY = 8000;

    const goTo = (idx) => {
      const target = (idx + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === target);
      });
      dots.forEach((dot, i) => {
        const active = i === target;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      current = target;
    };

    const startAuto = () => {
      if (REDUCED) return;
      stopAuto();
      autoTimer = setInterval(() => goTo(current + 1), AUTO_DELAY);
    };
    const stopAuto = () => {
      if (autoTimer) {
        clearInterval(autoTimer);
        autoTimer = null;
      }
    };

    if (prev) prev.addEventListener('click', () => { goTo(current - 1); startAuto(); });
    if (next) next.addEventListener('click', () => { goTo(current + 1); startAuto(); });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.ttwTestimonialDot, 10);
        goTo(idx);
        startAuto();
      });
    });

    // Pause on hover
    stage.addEventListener('mouseenter', stopAuto);
    stage.addEventListener('mouseleave', startAuto);

    // Touch swipe (mobile)
    let touchStartX = 0;
    let touchEndX = 0;
    stage.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      stopAuto();
    }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) goTo(current + 1);
        else goTo(current - 1);
      }
      startAuto();
    }, { passive: true });

    // Keyboard
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { goTo(current - 1); startAuto(); }
      if (e.key === 'ArrowRight') { goTo(current + 1); startAuto(); }
    });

    // Pause on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAuto();
      else startAuto();
    });

    startAuto();
  }

  /* ========================================================================
     INIT
     ======================================================================== */
  function init(root) {
    const r = root || document;
    const sections = r.querySelectorAll('[data-section-type="toth-trust-wall-pro"]');
    sections.forEach((section) => {
      initReveal(section);
      initCounters(section);
      initHeroes(section);
      initMarquee(section);
      initTestimonials(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.dataset.sectionType === 'toth-trust-wall-pro') {
      init(e.target.parentElement);
    }
  });

  document.addEventListener('shopify:section:select', (e) => {
    if (e.target && e.target.dataset.sectionType === 'toth-trust-wall-pro') {
      e.target.classList.add('is-visible');
    }
  });
})();
